import { AxleEvent } from '@/components/calendars/AxleCalendar';
import { DEEP_TEAL } from '@/constants/colors';
import { formatTimeRange } from '@/utils/datetimes/formatting';
import { gql } from '@apollo/client';
import { DropReason } from 'gql-types';
import moment from 'moment';
import { CSSProperties } from 'react';
import { EventPropGetter } from 'react-big-calendar';

import {
    ScheduleClinicianAppointmentFragment,
    ScheduleClinicianReservationFragment,
    ScheduleShiftBlockFragment,
    ScheduleShiftFragment,
    ScheduleTentativeShiftBlockFragment,
    ScheduleUnscheduledVisitFragment,
    ScheduleVisitChipFragment,
    TentativeClinicianAppointmentFragment,
} from './ClinicianSchedulesCalendarEvents.gql.types';

/*******************
 * Shifts
 ******************/
export const SCHEDULE_SHIFT_FRAGMENT = gql`
    fragment ScheduleShift on ShiftOccurrence {
        id
        startDatetime
        endDatetime
        clinician {
            id
            timezone
        }
        isCanceled
        recurrence {
            id
            dropReason
            serviceLimits {
                id
                service {
                    id
                }
                maxLimit
            }
        }
    }
`;

type ShiftCalendarEvent = AxleEvent & {
    type: CalendarEventType.SHIFT;
    event: ScheduleShiftFragment;
};

/*********************
 * Shift blocks
 *********************/
export const SCHEDULE_SHIFT_BLOCK_FRAGMENT = gql`
    fragment ScheduleShiftBlock on ShiftBlockOccurrence {
        id
        startDatetime
        endDatetime
        clinician {
            id
            firstName
            lastName
        }
        recurrence {
            id
            notes
            numAllClinicians
        }
    }
`;

type ShiftBlockCalendarEvent = AxleEvent & {
    type: CalendarEventType.SHIFT_BLOCK;
    event: ScheduleShiftBlockFragment;
};

export const getShiftBlockOccurrenceLabel = (shiftBlock: ScheduleShiftBlockFragment) => {
    return shiftBlock.recurrence.notes === ''
        ? shiftBlock.recurrence.numAllClinicians === 1
            ? `Shift Block for ${shiftBlock.clinician?.firstName ?? ''} ${
                  shiftBlock.clinician?.lastName ?? ''
              }`.trim()
            : 'Shift Block for multiple clinicians'
        : shiftBlock.recurrence.notes;
};

/*************************
 * Tentative shift blocks
 ************************/
export const SCHEDULE_TENTATIVE_SHIFT_BLOCK_FRAGMENT = gql`
    fragment ScheduleTentativeShiftBlock on TentativeShiftBlockOccurrence {
        id
        startDatetime
        endDatetime
        parentRequest {
            id
            requestingClinician {
                id
            }
            notes
        }
    }
`;

type TentativeShiftBlockCalendarEvent = AxleEvent & {
    type: CalendarEventType.TENTATIVE_SHIFT_BLOCK;
    event: ScheduleTentativeShiftBlockFragment;
};

/*************************
 * Clinician appointments
 *************************/
export const SCHEDULE_CLINICIAN_APPOINTMENT_FRAGMENT = gql`
    fragment ScheduleClinicianAppointment on ClinicianAppointment {
        id
        startDatetime
        endDatetime
        clinician {
            id
        }
        visit {
            id
            patient {
                id
                fullName
            }
        }
        encounters {
            id
            service {
                id
                name
            }
        }
    }
`;

type ClinicianAppointmentCalendarEvent = AxleEvent & {
    type: CalendarEventType.CLINICIAN_APPOINTMENT;
    event: ScheduleClinicianAppointmentFragment;
};

export const SCHEDULE_VISIT_CHIP_FRAGMENT = gql`
    fragment ScheduleVisitChip on ClinicianAppointment {
        id
        visit {
            id
            notesForClinician
            address {
                ...BasicAddress
            }
        }
    }
`;

export type ScheduleVisitChipAppointment = ScheduleClinicianAppointmentFragment &
    ScheduleVisitChipFragment;

/*************************
 * Clinician Reservations
 *************************/
export const SCHEDULE_CLINICIAN_RESERVATION_FRAGMENT = gql`
    fragment ScheduleClinicianReservation on ClinicianReservation {
        id
        clinician {
            id
            firstName
            lastName
        }
        startOffsetMinutes
        durationMinutes
        isVirtual
        startDatetime
        endDatetime
        visitReservation {
            id
            organization {
                id
            }
            externalId
            startDatetime
            expiryDatetime
            address {
                id
                address1
                address2
                city
                state
                zipcode
            }
            zipCode
            isFulfilled
        }
    }
`;

type ClinicianReservationCalendarEvent = AxleEvent & {
    type: CalendarEventType.CLINICIAN_RESERVATION;
    event: ScheduleClinicianReservationFragment;
};

/*******************
 * Unavailability
 ******************/
export const TENTATIVE_CLINICIAN_APPOINTMENT_FRAGMENT = gql`
    fragment TentativeClinicianAppointment on ClinicianAppointment {
        id
        durationMinutes
        startOffsetMinutes
        visit {
            id
            patient {
                id
                fullName
            }
        }
        encounters {
            id
            service {
                id
                name
            }
        }
    }
`;

export type TentativeClinicianAppointmentCalendarEvent = AxleEvent & {
    type: CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT;
    event: TentativeClinicianAppointmentFragment;
};

/*******************
 * Unavailability
 ******************/
export type UnavailabilityCalendarEvent = AxleEvent & {
    type: CalendarEventType.UNAVAILABILITY;
    event: {
        id: string;
        startDatetime: Date;
        endDatetime: Date;
        resourceId: string;
    };
};

/*******************
 * Event Clinician
 ******************/
export const SCHEDULE_CLINICIAN_FRAGMENT = gql`
    fragment ScheduleClinician on Clinician {
        id
        firstName
        lastName
        timezone
        logoUrl
        isActive
    }
`;

/*******************
 * Unscheduled Visits
 ******************/
export const SCHEDULE_UNSCHEDULED_VISIT_FRAGMENT = gql`
    fragment ScheduleUnscheduledVisit on Visit {
        id
        effectiveDate
        clinicianAppointments {
            id
            clinician {
                id
                fullName
            }
            services {
                id
                name
            }
            visit {
                id
                patient {
                    id
                    fullName
                }
            }
        }
    }
`;

export type ScheduleUnscheduledAppointment = NonNullable<
    ScheduleUnscheduledVisitFragment['clinicianAppointments']
>[number];

/*******************
 * Common
 ******************/
export type CalendarEvent = AxleEvent &
    (
        | ShiftCalendarEvent
        | ShiftBlockCalendarEvent
        | TentativeShiftBlockCalendarEvent
        | ClinicianAppointmentCalendarEvent
        | ClinicianReservationCalendarEvent
        | TentativeClinicianAppointmentCalendarEvent
        | UnavailabilityCalendarEvent
    );

export enum CalendarEventType {
    SHIFT = 'SHIFT',
    SHIFT_BLOCK = 'SHIFT_BLOCK',
    TENTATIVE_SHIFT_BLOCK = 'TENTATIVE_SHIFT_BLOCK',
    CLINICIAN_APPOINTMENT = 'CLINICIAN_APPOINTMENT',
    CLINICIAN_RESERVATION = 'CLINICIAN_RESERVATION',
    UNAVAILABILITY = 'UNAVAILABILITY',
    TENTATIVE_CLINICIAN_APPOINTMENT = 'TENTATIVE_CLINICIAN_APPOINTMENT',
}

export const unavailableBlockGradient = `
    repeating-linear-gradient(45deg,
        #666 10px,
        #666 12px,
        #111 12px,
        #111 21px
    )
`;

// Makes text readable against the unavailable-block gradient.
export const unavailableBlockTextShadow =
    '0 0 1px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.5), 1px 1px 2px rgba(0,0,0,0.5)';

export const multiClinicianSchedulesEventPropGetter: EventPropGetter<CalendarEvent> = ({
    type,
    event,
}: CalendarEvent) => {
    const style: CSSProperties = {
        borderRadius: '5px',
        color: 'white',
        border: '0px',
        fontSize: '11px',
        fontWeight: 600,
        textShadow: '0 1px 1px rgba(2,73,87,.4)',
        boxShadow: '0 0 2px rgba(31, 84, 85, 0.5)',
    };
    switch (type) {
        case CalendarEventType.SHIFT:
            // Check if shift is cancelled OR has a dropReason of BLOCKED or DROPPED
            if (
                event.isCanceled ||
                event.recurrence.dropReason === DropReason.BLOCKED ||
                event.recurrence.dropReason === DropReason.DROPPED
            ) {
                style.backgroundImage = unavailableBlockGradient;
            } else {
                style.opacity = 1;
                style.backgroundColor = 'rgba(0, 122, 117, 0.5)';
            }
            break;
        case CalendarEventType.SHIFT_BLOCK:
            style.backgroundImage = unavailableBlockGradient;
            style.textShadow = unavailableBlockTextShadow;
            break;
        case CalendarEventType.TENTATIVE_SHIFT_BLOCK:
            // TODO(jessica): Revisit this styling.
            style.backgroundImage = unavailableBlockGradient;
            style.border = '2px dashed #666';
            style.textShadow = unavailableBlockTextShadow;
            break;
        case CalendarEventType.CLINICIAN_APPOINTMENT:
            style.backgroundColor = DEEP_TEAL;
            break;
        case CalendarEventType.CLINICIAN_RESERVATION:
            style.backgroundColor = '#596cf2';
            break;
    }
    return {
        style,
    };
};

export const availabilityScheduleEventPropGetter: EventPropGetter<CalendarEvent> = ({
    type,
}: CalendarEvent) => {
    const style: CSSProperties = {
        borderRadius: '5px',
        color: 'white',
        border: '0px',
        fontSize: '11px',
    };
    switch (type) {
        case CalendarEventType.UNAVAILABILITY:
        case CalendarEventType.SHIFT_BLOCK:
            style.backgroundColor = '#D9D9D9';
            style.opacity = 0.5;
            style.borderRadius = '0';
            style.width = '100%';
            break;
        case CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT:
            style.backgroundColor = '#66C1BF';
            style.opacity = 1;
            break;
        case CalendarEventType.CLINICIAN_APPOINTMENT:
            style.backgroundColor = '#3D6B6B';
            style.opacity = 1;
            break;
        default:
            return {};
    }
    return {
        style,
    };
};

/******************************
 * Conversion helper functions
 ******************************/
export const clinicianAppointmentToCalendarEvent = (
    appointment: ScheduleClinicianAppointmentFragment,
): CalendarEvent => {
    const { id, visit, startDatetime, endDatetime, encounters, clinician } = appointment;
    const encounterNames = encounters.map((encounter) => encounter.service.name).join(', ');
    const title = visit?.patient.fullName
        ? `${visit.patient.fullName}-${encounterNames}`
        : encounterNames;
    return {
        type: CalendarEventType.CLINICIAN_APPOINTMENT,
        event: appointment,
        uuid: id,
        title,
        start: startDatetime ? new Date(startDatetime) : new Date(),
        end: endDatetime ? new Date(endDatetime) : new Date(),
        resourceId: clinician?.id ?? '',
    };
};

export const tentativeClinicianAppointmentToCalendarEvent = (
    appointment: TentativeClinicianAppointmentFragment,
    result: {
        clinicianId: string;
        tentativeStartDatetime: Date;
    },
): CalendarEvent => {
    const { id, visit, encounters } = appointment;
    const start = moment(result.tentativeStartDatetime).add(
        appointment.startOffsetMinutes,
        'minutes',
    );
    const end = start.clone().add(appointment.durationMinutes, 'minutes');
    return {
        type: CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT,
        event: appointment,
        uuid: id,
        title: `${visit?.patient.fullName ?? ''}-${encounters
            .map((encounter) => encounter.service.name)
            .join(', ')}`,
        start: start.toDate(),
        end: end.toDate(),
        resourceId: result.clinicianId,
    };
};

export const shiftToCalendarEvent = (shift: ScheduleShiftFragment): CalendarEvent => {
    const { id, startDatetime, endDatetime, clinician } = shift;
    const start = new Date(startDatetime);
    const end = new Date(endDatetime);
    return {
        type: CalendarEventType.SHIFT,
        event: shift,
        uuid: id,
        title: formatTimeRange(moment(start), moment(end), clinician.timezone),
        start,
        end,
        resourceId: clinician.id,
    };
};

export const shiftBlockToCalendarEvent = (
    shiftBlock: ScheduleShiftBlockFragment,
): CalendarEvent => {
    const { id, startDatetime, endDatetime, clinician } = shiftBlock;
    return {
        type: CalendarEventType.SHIFT_BLOCK,
        event: shiftBlock,
        uuid: id,
        title: getShiftBlockOccurrenceLabel(shiftBlock),
        start: new Date(startDatetime),
        end: new Date(endDatetime),
        resourceId: clinician?.id ?? '',
    };
};

export const tentativeShiftBlockToCalendarEvent = (
    tentativeShiftBlock: ScheduleTentativeShiftBlockFragment,
): CalendarEvent => {
    const {
        id,
        startDatetime,
        endDatetime,
        parentRequest: {
            notes,
            requestingClinician: { id: clinicianId },
        },
    } = tentativeShiftBlock;
    return {
        type: CalendarEventType.TENTATIVE_SHIFT_BLOCK,
        event: tentativeShiftBlock,
        uuid: id,
        title: `Tentative: ${notes}`,
        start: startDatetime,
        end: endDatetime,
        resourceId: clinicianId,
    };
};

export const clinicianReservationToCalendarEvent = (
    reservation: ScheduleClinicianReservationFragment,
): CalendarEvent => {
    const { id, clinician, startDatetime, endDatetime } = reservation;
    return {
        type: CalendarEventType.CLINICIAN_RESERVATION,
        event: reservation,
        uuid: id,
        title: `Reservation for ${clinician.firstName} ${clinician.lastName}`,
        start: startDatetime,
        end: endDatetime,
        resourceId: clinician.id,
    };
};

export const unavailabilityToCalendarEvent = (unavailability: {
    id: string;
    startDatetime: Date;
    endDatetime: Date;
    resourceId: string;
}): UnavailabilityCalendarEvent => {
    const { id, startDatetime, endDatetime, resourceId } = unavailability;
    return {
        type: CalendarEventType.UNAVAILABILITY,
        event: unavailability,
        uuid: id,
        title: '',
        start: startDatetime,
        end: endDatetime,
        resourceId,
    };
};
